'use client';

import {useEffect, useRef, useContext} from 'react';
import {useSession} from '@features/auth/authClient';
import {useScreenClipboard} from '../hooks/useScreenClipboard';
import {isEditableElementFocused} from '@features/utils/dom';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {useIsEditingTextRef} from '@features/frame/partialEdit/atoms';

/**
 * Component that handles Cmd+C/Cmd+V keyboard shortcuts for screen copy/paste.
 * Must be rendered inside ReactFlow to access the ReactFlow context.
 */
export function ClipboardKeyboardHandler() {
  const {readonly} = useContext(ReadonlyContext);
  const {isAuthenticated} = useSession();
  const {copy, paste} = useScreenClipboard();
  const isEditingTextRef = useIsEditingTextRef();

  // Use refs for stable event handler references
  const copyRef = useRef(copy);
  const pasteRef = useRef(paste);

  // Update refs in an effect to avoid lint warnings
  useEffect(() => {
    copyRef.current = copy;
  }, [copy]);

  useEffect(() => {
    pasteRef.current = paste;
  }, [paste]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // if the modal is open, don't handle the event
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('[role="dialog"]')
      )
        return;
      if (isEditableElementFocused()) return;
      if (isEditingTextRef.current) return;

      const key = event.key.toLowerCase();

      // Copy: Cmd+C / Ctrl+C (allowed in readonly for authenticated users)
      if (isAuthenticated && (event.metaKey || event.ctrlKey) && key === 'c') {
        event.preventDefault();
        copyRef.current();
      }

      // Paste: Cmd+V / Ctrl+V (blocked in readonly)
      if (!readonly && (event.metaKey || event.ctrlKey) && key === 'v') {
        event.preventDefault();
        void pasteRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [readonly, isAuthenticated, isEditingTextRef]);

  return null;
}
